import React, { useState, useEffect, useRef } from 'react';
import './AutocompleteInput.css';
import logoService from '../services/logoService';
import Icon from './Icon';
import { industryLabel } from './utils';

const AutocompleteInput = ({
  value = '',
  onChange,
  onSelect,
  onLoadMore,
  suggestions = [],
  loading = false,
  loadingMore = false,
  placeholder = '',
  totalCount = 0,
  hasMore = false,
  disabled = false,
  debounceMs = 300,
  multiSelect = false,
  selectedItems = [],
  showLogos = false,
  logoUrlKey = 'logo_url',
  fallbackLogoGenerator = null,
  itemType = 'item',
  currentPage = 1,
  pageSize = 20,
  onPageChange = null,
  showPagination = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionRefs = useRef([]);
  const suggestionsContainerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const getLogoUrl = (suggestion) => {
    if (!showLogos) return null;
    return logoService.getLogoUrl(suggestion);
  };

  const getFallbackLogo = (suggestion) => {
    if (fallbackLogoGenerator) {
      return fallbackLogoGenerator(suggestion);
    }
    
    const name = suggestion.name || suggestion.title || 'Item';
    return logoService.getFallbackLogoUrl(name, { size: 32 });
  };

  const handleLogoError = (event, suggestion) => {
    logoService.handleLogoError(event, suggestion, { size: 32 });
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHighlightedIndex(-1);
    setShowSuggestions(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onChange?.(newValue);
    }, debounceMs);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSuggestionSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    if (multiSelect) {
      onSelect?.(suggestion);
    } else {
      setInputValue(suggestion.name);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      onSelect?.(suggestion);
    }
  };

  const isSelected = (suggestion) => {
    if (!multiSelect) return false;
    return selectedItems.some(item => item.id === suggestion.id);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  // Handle blur with delay to allow for suggestion clicks
  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }, 150);
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionRefs.current[highlightedIndex]) {
      suggestionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  const handleScroll = (e) => {
    if (!hasMore || loadingMore || !onLoadMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const threshold = 50;
    
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      onLoadMore();
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    onPageChange?.(newPage);
  };

  return (
    <div className="autocomplete-container">
      <div className="autocomplete-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`autocomplete-input ${loading ? 'loading' : ''}`}
        />
        {loading && <div className="autocomplete-spinner" />}
        {totalCount > 0 && (
          <div className="autocomplete-count">
            {totalCount} options
          </div>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="autocomplete-suggestions" onScroll={handleScroll} ref={suggestionsContainerRef}>
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.id || suggestion.name}
              ref={el => suggestionRefs.current[index] = el}
              className={`autocomplete-suggestion ${
                index === highlightedIndex ? 'highlighted' : ''
              } ${
                isSelected(suggestion) ? 'selected' : ''
              }`}
              onClick={() => handleSuggestionSelect(suggestion)}
            >
              {showLogos && (
                <div className="suggestion-logo">
                  <img 
                    src={getLogoUrl(suggestion) || getFallbackLogo(suggestion)} 
                    alt={`${suggestion.name} logo`}
                    className="suggestion-logo-image"
                    onError={(e) => handleLogoError(e, suggestion)}
                  />
                </div>
              )}
              
              <div className="suggestion-content">
                <div className="suggestion-main">
                  <span className="suggestion-name">
                    {isSelected(suggestion) && <span className="selected-indicator"><Icon name="check" /> </span>}
                    {suggestion.name}
                  </span>
                  {suggestion.contact_count && (
                    <span className="suggestion-count">({suggestion.contact_count})</span>
                  )}
                </div>
                
                {(suggestion.industry || suggestion.sector || suggestion.total_contacts) && (
                  <div className="suggestion-meta">
                    {suggestion.industry && (
                      <span className="suggestion-industry">{industryLabel(suggestion.industry)}</span>
                    )}
                    {suggestion.sector && suggestion.sector !== suggestion.industry && (
                      <span className="suggestion-sector"> • {suggestion.sector}</span>
                    )}
                    {suggestion.total_contacts && (
                      <span className="suggestion-total-contacts"> • {suggestion.total_contacts} total contacts</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loadingMore && (
            <div className="autocomplete-loading-more">
              <div className="autocomplete-spinner" />
              Loading more...
            </div>
          )}
          
          {showPagination && totalPages > 1 && (
            <div className="autocomplete-pagination">
              <div className="pagination-info">
                Showing {startItem}-{endItem} of {totalCount} {itemType}s
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  <Icon name="chevron-left" /> Prev
                </button>
                <span className="pagination-current">
                  {currentPage} of {totalPages}
                </span>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next <Icon name="chevron-right" />
                </button>
              </div>
            </div>
          )}
          
          {!showPagination && totalCount > suggestions.length && !loadingMore && (
            <div className="autocomplete-more-info">
              Showing {suggestions.length} of {totalCount} options
              {hasMore && onLoadMore && (
                <button 
                  className="autocomplete-load-more-btn"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                >
                  Load More
                </button>
              )}
            </div>
          )}
          
          {!showPagination && !hasMore && totalCount > suggestions.length && (
            <div className="autocomplete-end-info">
              Showing {suggestions.length} of {totalCount} options (scroll up to see more)
            </div>
          )}
        </div>
      )}
      
      {showSuggestions && suggestions.length === 0 && !loading && inputValue.trim() && (
        <div className="autocomplete-suggestions">
          <div className="autocomplete-no-results">
            No matches found for "{inputValue}"
          </div>
        </div>
      )}
      
      {showPagination && totalPages > 1 && !showSuggestions && (
        <div className="autocomplete-standalone-pagination">
          <div className="pagination-info">
            Showing {startItem}-{endItem} of {totalCount} {itemType}s
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-button" 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              title="Previous page"
            >
              <Icon name="chevron-left" /> Prev
            </button>
            <span className="pagination-current">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              className="pagination-button" 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
              title="Next page"
            >
              Next <Icon name="chevron-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
